import { notification } from 'antd';
import { EditorState } from 'draft-js';
import { useEffect, useMemo, useState } from 'react';

import { htmlToEditorState } from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import {
  useGetGeneByIdQuery,
  useUpdateGeneMutation,
} from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';

export interface UseGeneEditReportTextModalLogicProps {
  geneId: number;
  onSaved?: () => void;
  onClose: () => void;
  stageType?: string;
}

/**
 * Custom hook that encapsulates all the business logic for GeneEditReportTextModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useGeneEditReportTextModalLogic = ({
  geneId,
  onSaved,
  onClose,
  stageType = VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
}: UseGeneEditReportTextModalLogicProps) => {
  const {
    user,
    stageInfo,
    createModalOpts,
    getIsSaveDisabled: baseGetIsSaveDisabled,
    isReadOnly,
  } = useModalForm({
    onClose,
    successMessage: 'Gene updated successfully',
    stageType,
    requireWorkflowEdit: false,
  });

  // Read the existing gene entry from the RTK Query cache.
  // Because <GeneEditReportTextModal> and its parent both call the same hook
  // with the same arg, RTK Query reuses the cached data instead of firing a second request
  const { data: gene, isLoading: loading } = useGetGeneByIdQuery(
    { gene_id: geneId },
    { skip: !geneId },
  );
  // Mutation hook returns a [trigger, result] tuple.
  // After a successful save, invalidatesTags automatically refetches any query that provided the matching tag.
  const [updateGeneMutation] = useUpdateGeneMutation();

  const [saving, setSaving] = useState<boolean>(false);

  // Rich text editor state for disorders
  const initialDisordersEditorState = useMemo(
    () => htmlToEditorState(gene?.report_text_disorders || ''),
    [gene?.report_text_disorders],
  );
  const [disordersEditorState, setDisordersEditorState] = useState<EditorState>(
    initialDisordersEditorState,
  );

  // initial values (free text fields stored as string)
  const initialValues = useMemo(
    () => ({
      report_text_protein: gene?.report_text_protein || '',
      report_text_function: gene?.report_text_function || '',
      // Use the original HTML from the backend to avoid formatting loss through Draft.js roundtrip
      report_text_disorders: gene?.report_text_disorders || '',
    }),
    [
      gene?.report_text_protein,
      gene?.report_text_function,
      gene?.report_text_disorders,
    ],
  );

  useEffect(() => {
    // keep editor state in sync when gene changes
    setDisordersEditorState(initialDisordersEditorState);
  }, [initialDisordersEditorState]);

  const modalOpts = {
    ...createModalOpts('Edit gene for report text'),
  };

  // Wrap base getIsSaveDisabled to include saving state
  const getIsSaveDisabled = (isValid: boolean, dirty: boolean) => {
    return baseGetIsSaveDisabled(isValid, dirty) || saving;
  };

  // updateGeneMutation() calls PUT /variantdata/gene/:gene_id
  // After success: invalidateTags automatically is called --> refetching of tables
  // user sees confirmation, modal closes
  const handleSubmit = async (values: typeof initialValues) => {
    if (saving) {
      return;
    }
    try {
      setSaving(true);
      await updateGeneMutation({
        gene_id: geneId,
        requestData: {
          report_text_protein: values.report_text_protein,
          report_text_function: values.report_text_function,
          report_text_disorders: values.report_text_disorders,
        },
      }).unwrap();

      notification.success({
        message: 'Success!',
        description: 'Gene updated successfully',
      });

      if (onSaved) {
        onSaved();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return {
    loading,
    saving,
    user,
    isReadOnly,
    stageInfo,
    gene,
    disordersEditorState,
    setDisordersEditorState,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleSubmit,
  };
};

export default useGeneEditReportTextModalLogic;
